import { useMessage } from "@hermes/intl";
import {
  ButtonV2,
  colors,
  createStaticStyles,
  cssFns,
  Modal,
  useRadioGroupState,
  useSelectState,
  useToggleSwitchState,
} from "@hermes/ui";
import { useFormFields } from "@hermes/ui/src/fields/common/form";
import { Temporal } from "@hermes/utils";
import { useState } from "react";

import type { DoctorListFiltersProps } from ".";
import { ChildShort } from "./short-filters/child";
import { DateShort } from "./short-filters/date";
import { NearmeShort } from "./short-filters/nearme";
import { ShortSort } from "./short-filters/sort";

const styles = {
  container: createStaticStyles.clean([
    cssFns.padding(16, 10),
    { display: "grid", gap: 16 },
  ]),
  footer: createStaticStyles.clean([
    {
      display: "grid",
      gap: 10,
      gridTemplateColumns: "auto auto",
    },
    cssFns.padding(20, 10),
  ]),
};

export const containerBorderBlockEndStyles = createStaticStyles.clean({
  borderBlockEnd: `1px solid ${colors.principal.grayline}`,
});

export const QuickModalFilters = ({
  filtersModalState,
  params,
  isChild,
  onParamsChange,
  onChildChange,
}: DoctorListFiltersProps) => {
  const [date, setDate] = useState<Temporal.PlainDate | undefined>(params.date);

  const fields = useFormFields({
    sort: useRadioGroupState({
      value: params.sort || "default",
      required: false,
    }),
    child: useToggleSwitchState({
      value: isChild ?? false,
      required: false,
    }),
    childAge: useSelectState<number>({
      value: params.child_age ?? null,
    }),
    nearme: useToggleSwitchState({
      value: params.nearme ?? false,
    }),
  });
  const message = useMessage();

  const onSubmit = () => {
    isChild !== fields.child.value && onChildChange?.();
    filtersModalState?.close();

    requestAnimationFrame(() => {
      onParamsChange({
        ...params,
        sort: fields.sort.value ?? "default",
        nearme: fields.nearme.value,
        date: date,
        child_age: fields.childAge.value ?? undefined,
      });
    });
  };

  const onResetButtonClick = () => {
    fields.child.onChange({ value: false });
    fields.childAge.onChange({ value: null });
    fields.nearme.onChange({ value: false });
    fields.sort.onChange({ value: "default" });
    setDate(undefined);
  };

  const onClose = () => {
    filtersModalState?.close();
  };

  return (
    filtersModalState && (
      <Modal
        state={filtersModalState}
        size="xl"
        title={message({ id: "common.filters", default: "Filters" })}
        onResetButtonClick={onResetButtonClick}
        main={
          <div css={styles.container}>
            <ShortSort field={fields.sort} />
            <ChildShort
              childField={fields.child}
              childAgeField={fields.childAge}
            />
            <NearmeShort field={fields.nearme} />
            <DateShort date={date} onDateChange={setDate} />
          </div>
        }
        footer={
          <div css={styles.footer}>
            <ButtonV2
              variant="outlined"
              text={message({
                id: "common.cancel",
              })}
              onPress={onClose}
            />
            <ButtonV2
              variant="solid"
              text={message({
                id: "9db59481-22a6-4d7c-806f-ae8f4ff52a34",
              })}
              onPress={onSubmit}
            />
          </div>
        }
        isFooterSticky
      />
    )
  );
};
